// „Kontakt zuweisen" — die Verbindung einer neuen Person zu einer Firma.
//
// Seit 01.10.2026 (User: „hier darf nicht ‚Funktion' stehen, sondern
// ‚Verantwortlichkeit', dann das Dropdown, und dann ist er Mensch im
// Unternehmen — auch andersherum, wenn privat + Firma") benutzt das Fenster
// dieselbe Eingabe wie die Vollanlage bei „Verbindung zu Unternehmen":
// `VerantwortlichkeitInput` (Typ intern/extern, Katalog-Dropdown, Haken
// „Mensch im Unternehmen"). Hier steht nur die reine Ableitung — ohne React,
// damit sie per Vitest prüfbar ist.
//
// Die Regel ist die der Vollanlage, nicht eine eigene:
//   • Jede gewählte Verantwortlichkeit wird eine Verbindung „Mensch fürs
//     Unternehmen" mit der Verantwortlichkeit als `function` (Geschäftsführer
//     und Gesellschafter: eigene Beziehungstypen) — persistRelations
//     `relationBodies("personFirma")` bzw. `verantToItems` für attach-menschen.
//   • „Mensch im Unternehmen" ist der Haken an der Verbindung. Er folgt der
//     gewählten Verantwortlichkeit (Katalog-/Matrixspalte
//     `istMenschImUnternehmen`, z. B. Geschäftsführer ja, Gesellschafter nein),
//     entfällt bei „extern" und lässt sich von Hand setzen
//     (menschImUnternehmen.ts).
//   • Der Kontaktstatus „Verantwortung fürs Unternehmen" entsteht bei JEDER
//     Verbindung zu einer Firma — als Primärstatus beim neuen Ansprechpartner
//     (attach-menschen), als Zusatzstatus beim Privatkontakt
//     (ContactStatusLinkService.ensureDerived), dessen gewählter Status bleibt.

import type { Verantwortlichkeit } from "../../services/verantwortlichkeitService";
import type { VerantRole, VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";

export const STATUS_VERANTWORTUNG = "Verantwortung fürs Unternehmen";
export const VERBINDUNG_IM = "Mensch im Unternehmen";
export const VERBINDUNG_FUERS = "Mensch fürs Unternehmen";

/** Der leere Startwert — wie ein neuer Eintrag der Vollanlage. */
export const LEERE_VERANTWORTLICHKEIT: VerantwortlichkeitValue = {
    isExtern: false,
    roles: [],
    externalCompany: null,
};

const norm = (s: string) => s.trim().toLocaleLowerCase("de-DE").replace(/\s+/g, " ");

/**
 * T-36 „Funktion aus der Signatur" → Verantwortlichkeit. NUR ein Katalogtreffer
 * wird vorgewählt — geraten wird nicht: „Sachbearbeiterin" steht in keinem
 * Katalog und bleibt deshalb leer (das Fenster zeigt den Text als Hinweis).
 *
 * Verglichen wird ohne Groß-/Kleinschreibung; die weibliche Form auf „-in"
 * zählt mit („Prokuristin" → „Prokurist"). Nur interne Einträge: Ein Absender
 * schreibt in SEINE Signatur, was er in seiner Firma ist.
 */
export function verantAusSignatur(
    text: string | null | undefined,
    katalog: ReadonlyArray<Pick<Verantwortlichkeit, "bezeichnung" | "art" | "aktiv" | "schluessel">>,
): VerantwortlichkeitValue | null {
    const t = norm(text ?? "");
    if (!t) return null;
    const treffer = katalog.find((k) => {
        if (k.art !== "intern" || k.aktiv === false) return false;
        const b = norm(k.bezeichnung);
        return b === t || `${b}in` === t;
    });
    if (!treffer) return null;
    const rolle: VerantRole =
        treffer.schluessel === "GESCHAEFTSFUEHRER"
            ? { kind: "gf" }
            : treffer.schluessel === "GESELLSCHAFTER"
              ? { kind: "gesellschafter" }
              : { kind: "mensch", verantwortlichkeit: treffer.bezeichnung };
    return { ...LEERE_VERANTWORTLICHKEIT, roles: [rolle] };
}

/** Ist er „Mensch im Unternehmen"? Der Haken — bei extern nie. */
export function istMenschImUnternehmen(v: VerantwortlichkeitValue | null | undefined): boolean {
    return !!v && !v.isExtern && !!v.istMenschImUnternehmen;
}

/** Hat die Eingabe überhaupt eine Aussage (Rolle oder Haken)? */
export function hatVerbindungsAngabe(v: VerantwortlichkeitValue | null | undefined): boolean {
    return !!v && ((v.roles?.length ?? 0) > 0 || istMenschImUnternehmen(v));
}

/** Die gewählten Verantwortlichkeiten als Text („Geschäftsführer, Einkauf"). */
export function rollenText(v: VerantwortlichkeitValue | null | undefined): string {
    return (v?.roles ?? [])
        .map((r) => (r.kind === "gf" ? "Geschäftsführer" : r.kind === "gesellschafter" ? "Gesellschafter" : r.verantwortlichkeit))
        .filter((s) => !!s && s.trim())
        .join(", ");
}

/** Art der Verbindung, wie die Akte sie nennt: im (arbeitet dort) oder fürs Unternehmen. */
export function verbindungsArt(v: VerantwortlichkeitValue | null | undefined): typeof VERBINDUNG_IM | typeof VERBINDUNG_FUERS {
    return istMenschImUnternehmen(v) ? VERBINDUNG_IM : VERBINDUNG_FUERS;
}

/**
 * Was beim Speichern tatsächlich entsteht — für den Hinweis unter dem Feld.
 *
 * `fall: "ap"` — neuer Ansprechpartner (attach-menschen): Primärstatus
 * „Verantwortung fürs Unternehmen". `fall: "privat"` — Privatkontakt mit
 * Verbindung zur Firma: der gewählte Status bleibt, „Verantwortung fürs
 * Unternehmen" kommt als Zusatzstatus dazu.
 */
export function verbindungsHinweis(
    v: VerantwortlichkeitValue | null | undefined,
    fall: { typ: "ap" } | { typ: "privat"; statusName: string },
): { verbindung: string; status: string } {
    const rollen = rollenText(v);
    const art = verbindungsArt(v);
    const extern = v?.isExtern ? ", extern" : "";
    const verbindung = rollen
        ? `„${art}" (${rollen}${extern})`
        : istMenschImUnternehmen(v)
          ? `„${art}" (ohne Verantwortlichkeit)`
          : fall.typ === "ap"
            ? `„${VERBINDUNG_FUERS}" (ohne Verantwortlichkeit)`
            : "";
    const status =
        fall.typ === "ap"
            ? `Kontaktstatus „${STATUS_VERANTWORTUNG}"`
            : `Status „${fall.statusName}" bleibt der Hauptstatus; „${STATUS_VERANTWORTUNG}" kommt als Zusatzstatus dazu`;
    return { verbindung, status };
}
